import { Button, Flex } from '@cognite/cogs.js';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import projectSlice from 'redux/reducers/project';
import type { Project } from 'types';

export type ResolveActionProps = {
  project: Project;
  user: string;
  isProjectResolved: boolean;
  isWorkStepsResolved: boolean;
  viewOnly: boolean;
  shouldRenderNewCommentFlow: boolean;
};

export const ResolveAction = ({
  project,
  user,
  isProjectResolved,
  isWorkStepsResolved,
  viewOnly,
  shouldRenderNewCommentFlow,
}: ResolveActionProps) => {
  const dispatch = useDispatch();

  const projectViewOnly = useMemo(
    () => viewOnly || project.status === 'done',
    [viewOnly, project.status]
  );

  const isCompilerResolved = Boolean(project.resolved?.compiler?.date);

  const isProjectInReviewPhase = useMemo(
    () => project.status === 'review',
    [project.status]
  );

  const shallResolveActionBeVisible = useMemo(
    () => !projectViewOnly && (!isProjectResolved || !isWorkStepsResolved),
    [projectViewOnly, isProjectResolved, isWorkStepsResolved]
  );

  const handleResolveClick = useCallback(() => {
    dispatch(
      projectSlice.actions.resolveComments({
        resolved: {
          name: user,
          date: Date.now(),
        },
      })
    );
  }, [dispatch, user]);

  if (!shallResolveActionBeVisible) {
    return null;
  }

  return (
    <Flex direction="row" alignItems="center" justifyContent="space-between">
      {!shouldRenderNewCommentFlow && (
        <Button
          aria-label="dashboard-resolve"
          type="secondary"
          disabled={!isWorkStepsResolved}
          onClick={handleResolveClick}
        >
          {isCompilerResolved && isProjectInReviewPhase
            ? 'Confirm resolution'
            : 'Resolve'}
        </Button>
      )}
    </Flex>
  );
};
